import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Check, GripVertical, RotateCcw } from "lucide-react";

const KEY = "lms_leads_columns_v1";

// Full column catalog. `default` = shown by default.
export const COLUMN_CATALOG = [
  { key: "name", label: "Name", default: true },
  { key: "category", label: "Category", default: true },
  { key: "stage", label: "Stage", default: true },
  { key: "course_interest", label: "Course", default: true },
  { key: "source", label: "Source", default: true },
  { key: "phone", label: "Phone", default: true },
  { key: "created_at", label: "Created Date", default: true },
  { key: "email", label: "Email", default: false },
  { key: "next_followup_at", label: "Next Follow-up", default: false },
  { key: "campaign_name", label: "Campaign", default: false },
  { key: "adset_name", label: "Ad Set", default: false },
  { key: "ad_name", label: "Ad", default: false },
  { key: "keyword", label: "Keyword", default: false },
  { key: "source_channel", label: "Channel", default: false },
  { key: "referred_by", label: "Referred By", default: false },
  { key: "budget", label: "Budget", default: false },
  { key: "education_level", label: "Education", default: false },
  { key: "career_goal", label: "Career Goal", default: false },
  { key: "summary", label: "Summary", default: false },
  { key: "lead_id", label: "Lead ID", default: false },
  { key: "platform", label: "Platform", default: false },
];

const KEY_TO_LABEL = Object.fromEntries(COLUMN_CATALOG.map((c) => [c.key, c.label]));

function defaultConfig() {
  return COLUMN_CATALOG.map((c) => ({ key: c.key, visible: !!c.default }));
}

export function loadColumns() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return defaultConfig();
    const parsed = JSON.parse(raw);
    // Reconcile against catalog: keep saved order + visibility, append any new keys at the end using their catalog defaults
    const seen = new Set();
    const out = [];
    for (const item of parsed) {
      if (KEY_TO_LABEL[item.key]) { out.push({ key: item.key, visible: !!item.visible }); seen.add(item.key); }
    }
    for (const c of COLUMN_CATALOG) {
      if (!seen.has(c.key)) out.push({ key: c.key, visible: !!c.default });
    }
    return out;
  } catch { return defaultConfig(); }
}

export function saveColumns(cfg) {
  try { localStorage.setItem(KEY, JSON.stringify(cfg)); } catch {}
}

export default function ColumnPicker({ open, onClose, config, onChange }) {
  const [draft, setDraft] = useState(config);
  const [dragKey, setDragKey] = useState(null);

  useEffect(() => { if (open) setDraft(config); }, [open, config]);

  const commit = (next) => {
    setDraft(next);
    onChange(next);
    saveColumns(next);
  };

  const toggle = (key) => {
    commit(draft.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c)));
  };

  const reset = () => commit(defaultConfig());

  const onDragStart = (e, key) => { setDragKey(key); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetKey) => {
    if (!dragKey || dragKey === targetKey) return;
    const next = [...draft];
    const from = next.findIndex((c) => c.key === dragKey);
    const to = next.findIndex((c) => c.key === targetKey);
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setDragKey(null);
    commit(next);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="column-picker-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Edit Columns</span>
            <button
              onClick={reset}
              data-testid="column-picker-reset"
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-[6px] border border-[#6C47FF] font-[600]"
            >
              <RotateCcw className="h-3 w-3" /> Reset
            </button>
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[#6B7280] -mt-2">Drag rows to reorder. Check to show, uncheck to hide. Changes save automatically.</p>
        <div className="space-y-1 max-h-[60vh] overflow-y-auto">
          {draft.map((c) => (
            <div
              key={c.key}
              draggable
              onDragStart={(e) => onDragStart(e, c.key)}
              onDragOver={onDragOver}
              onDrop={() => onDrop(c.key)}
              data-testid={`column-picker-row-${c.key}`}
              className={`flex items-center gap-2 px-2 py-2 rounded-[6px] border border-[#E8EAEF] bg-white cursor-move ${dragKey === c.key ? "opacity-40" : ""}`}
            >
              <GripVertical className="h-4 w-4 text-[#c3c1bd] flex-shrink-0" />
              <button
                onClick={() => toggle(c.key)}
                data-testid={`column-picker-toggle-${c.key}`}
                className="flex-shrink-0 h-4 w-4 rounded-[3px] border flex items-center justify-center"
                style={{ borderColor: c.visible ? "#6C47FF" : "#c3c1bd", backgroundColor: c.visible ? "#6C47FF" : "#fff" }}
              >
                {c.visible && <Check className="h-3 w-3 text-white" />}
              </button>
              <span className="text-sm text-[#1A1A2E] flex-1">{KEY_TO_LABEL[c.key] || c.key}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
